import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { XbrlWorkerBridgeService } from '../../engine/xbrl-worker-bridge.service';
import { JobStatusLineComponent } from '../../shared/components/job-status-line/job-status-line.component';

type WorkerStatus = 'idle' | 'ready' | 'unsupported' | 'error';
type PyodideStatus = 'idle' | 'loading' | 'ready' | 'error';
type ArelleStatus = 'idle' | 'packages' | 'micropip' | 'import' | 'ready' | 'error';
type TaxonomyStatus = 'idle' | 'fetch' | 'write' | 'register' | 'ready' | 'error';
type ValidateStatus = 'idle' | 'fetch' | 'write' | 'load' | 'validate' | 'ready' | 'error';
type ValidatePhase = 'fetch' | 'write' | 'load' | 'validate';
type LightLoadStatus = 'idle' | 'fetch' | 'write' | 'parse' | 'labels' | 'ready' | 'error';
type LightLoadPhase = 'fetch' | 'write' | 'parse' | 'labels';
type TrendingTestStatus =
  | 'idle'
  | 'fetch'
  | 'write'
  | 'parse'
  | 'labels'
  | 'ready'
  | 'error';
type TrendingTestPhase = 'fetch' | 'write' | 'parse' | 'labels';
type ReportsStatus =
  | 'idle'
  | 'fetch'
  | 'write'
  | 'parse'
  | 'indicators'
  | 'render'
  | 'write-xlsx'
  | 'ready'
  | 'error';
type ReportsPhase = 'fetch' | 'write' | 'parse' | 'indicators' | 'render' | 'write-xlsx';

export type ReportsDebugRow = {
  entityLei: string;
  periodEnd: string;
  entryPoint: string;
  template: string;
  table: string;
  columnNr: string;
  rowNr: string;
  value: string | number | null;
  variableId: string;
  dataType: string;
  valueKind: string;
  unit: string;
  decimals: string;
};

type ReportsSignatureStats = {
  factsChecked: number;
  factsWithDimSignature: number;
  factsMatchedToTable: number;
  factsAmbiguousMetric: number;
  factsNoTableSignature: number;
  factsNoMetricProperty?: number;
  dpmIndexSize: number;
  rowsMaterialized?: number;
  cellsFilled?: number;
  factsOtherTable?: number;
  factsTableMetric?: number;
};

export type ReportsOpenTableDiagnostic = {
  tableCode: string;
  mode: string;
  atlOpenLayoutDetected: boolean;
  openDefCount: number;
  openColumnCount: number;
  tablePropertyIds: number[];
  rowKeyPropertyIds: number[];
  rowKeyMetricCodes: string[];
  openDefSummary: Array<{
    propertyId: number;
    metricCode: string;
    defCount: number;
    signatureCount: number;
    sampleSignatures: string[];
  }>;
  contextCount: number;
  instanceFactCount: number;
  factsWithTableMetric: number;
  matchedFacts: Array<Record<string, unknown>>;
  unmatchedFacts: Array<Record<string, unknown>>;
  factsNoMetricPropertyCount: number;
  factsNoMetricPropertySample: Array<Record<string, unknown>>;
  instanceMetricsOverview: Array<{
    metricCode: string;
    propertyId: number | null;
    inOpenTable: boolean;
    factCount: number;
  }>;
};

export type ReportsMetricMatchRow = {
  factQname: string;
  contextId: string;
  effectiveValue: string | number | null;
  metricCode: string;
  propertyIdFromDpm: number;
  signature: string;
  resolvedVariableId: string | null;
  reason?: string;
  indexKey?: string;
};

export type ReportsMetricDiagnostic = {
  tableCode: string;
  indexKeyFormat: string;
  dpmIndexRows: Array<{
    variableId: string;
    propertyId: number;
    signature: string;
    cellCode: string;
  }>;
  sharedSignatureBuckets: Record<
    string,
    Array<{ propertyId: number; variableId: string }>
  >;
  matchedFacts: ReportsMetricMatchRow[];
  unmatchedFacts: ReportsMetricMatchRow[];
};

export type ReportsDebugTableStat = {
  template: string;
  table: string;
  atlSheet: string;
  atlSlots: number;
  valuesFilled: number;
  signatureStats: ReportsSignatureStats;
  openTable?: boolean;
  metricDiagnostics?: ReportsMetricDiagnostic;
  openTableDiagnostics?: ReportsOpenTableDiagnostic;
};

export type ReportsDebugStats = {
  entityLei: string;
  periodEnd: string;
  entryPoint: string;
  instanceFactCount: number;
  tables: ReportsDebugTableStat[];
};

/** Console analysis for metadata / shared-signature tables (compact + expandable). */
export function logReportsMetricDiagnostics(stats: ReportsDebugStats | null): void {
  if (!stats?.tables?.length) return;
  const withDiag = stats.tables.filter((t) => t.metricDiagnostics);
  if (!withDiag.length) return;

  console.group('[reports] metric diagnostics (DPM PropertyID + signature)');
  for (const t of withDiag) {
    const d = t.metricDiagnostics!;
    const matched = d.matchedFacts ?? [];
    const unmatched = d.unmatchedFacts ?? [];
    const buckets = d.sharedSignatureBuckets ?? {};
    console.groupCollapsed(
      `${t.table} — ${matched.length} matched, ${unmatched.length} unmatched, ${Object.keys(buckets).length} shared sig bucket(s)`,
    );
    const row = (r: ReportsMetricMatchRow) => ({
      metric: r.metricCode,
      propertyId: r.propertyIdFromDpm,
      signature: r.signature || '(empty)',
      variableId: r.resolvedVariableId ?? '—',
      fact: r.factQname,
      value: r.effectiveValue,
      reason: r.reason ?? 'ok',
    });
    if (matched.length) {
      console.info('Matched');
      console.table(matched.map(row));
    }
    if (unmatched.length) {
      console.info('Unmatched');
      console.table(unmatched.map(row));
    }
    console.info('DPM index (propertyId, signature → variableId)', d.dpmIndexRows);
    console.debug('Shared signature buckets', buckets);
    console.groupEnd();
  }
  console.groupEnd();
}

/** Console RCA for open-row tables (C_27–C_29): metrics, signatures, materialization. */
export function logReportsOpenTableDiagnostics(stats: ReportsDebugStats | null): void {
  if (!stats?.tables?.length) return;
  const openTables = stats.tables.filter((t) => t.openTableDiagnostics);
  if (!openTables.length) return;

  console.group('[reports] open-table diagnostics (RCA)');
  for (const t of openTables) {
    const d = t.openTableDiagnostics!;
    const matched = d.matchedFacts ?? [];
    const unmatched = d.unmatchedFacts ?? [];
    const rowsMat = t.signatureStats.rowsMaterialized ?? 0;
    console.groupCollapsed(
      `${t.table} — ${rowsMat} row(s) materialized, ${matched.length} fact(s) matched, ${unmatched.length} table-metric sig mismatch, ${d.factsWithTableMetric} table-metric fact(s) total`,
    );
    console.info('Summary', {
      atlOpenLayout: d.atlOpenLayoutDetected,
      openDefs: d.openDefCount,
      openColumns: d.openColumnCount,
      contexts: d.contextCount,
      rowKeyMetrics: d.rowKeyMetricCodes,
      slots: t.atlSlots,
      filled: t.valuesFilled,
      stats: t.signatureStats,
    });
    console.info('DPM open column metrics (expected for this table)', d.openDefSummary);
    const inTable = (d.instanceMetricsOverview ?? []).filter((m) => m.inOpenTable);
    const notInTable = (d.instanceMetricsOverview ?? []).filter((m) => !m.inOpenTable);
    if (inTable.length) {
      console.info('Instance metrics that belong to this open table', inTable);
    } else {
      console.warn(
        'No instance facts use metrics from this table — filing may not contain data for these columns.',
      );
    }
    if (notInTable.length) {
      console.info('Other instance metrics (sample)', notInTable.slice(0, 20));
    }
    if (matched.length) {
      console.info('Matched (property + signature → column)');
      console.table(
        matched.map((r) => ({
          metric: r['metricCode'],
          propertyId: r['propertyId'],
          context: r['contextId'],
          instanceSig: r['instanceSignature'] || '(none)',
          columns: (r['matchedColumns'] as string[])?.join(', '),
          value: r['effectiveValue'],
        })),
      );
    }
    if (unmatched.length) {
      console.info('Signature mismatch (table metric but context ≠ DPM def)');
      console.table(
        unmatched.map((r) => ({
          metric: r['metricCode'],
          propertyId: r['propertyId'],
          context: r['contextId'],
          instanceSig: r['instanceSignature'] || '(none)',
          expectedCount: r['expectedSignatureCount'],
          unresolved: (r['unresolvedMembers'] as string[])?.join('; '),
          value: r['effectiveValue'],
        })),
      );
      console.debug('First mismatch — expected DPM signatures', unmatched[0]?.['expectedSignatures']);
    }
    if (!matched.length && !unmatched.length) {
      console.warn(
        'Zero facts with table metrics. Check instanceMetricsOverview and filing indicators.',
      );
    }
    console.groupEnd();
  }
  console.groupEnd();
}

export type LightLoadSampleFact = {
  qname: string;
  namespace: string;
  localName: string;
  value: string;
  contextRef: string;
  unitRef: string | null;
  decimals: string | null;
  label: string | null;
};

export type TaxonomyPackageInfo = {
  key: string;
  name: string;
  identifier: string;
  version: string;
  publisher: string;
  publicationDate: string;
  entryPointCount: number;
  remappingCount: number;
  bytes: number;
  vfsPath: string;
};

export type ValidationMessage = {
  level: string;
  code: string;
  text: string;
  refs: Array<{ href?: string; sourceLine?: number | string }>;
};

export type ProfileStat = {
  name: string;
  ms: number;
  memKB: number | null;
};

export type ValidateException = {
  type: string;
  message: string;
  traceback: string;
};

export type SampleFiling = {
  label: string;
  family: string;
  period: string;
  url: string;
  filename: string;
  sizeBytes: number;
  note?: string;
};

const TD = '/test-data/202612/';

// Spike step 5: empirical per-framework measurements.
// Order matters: groups appear in the UI in insertion order of SAMPLE_FILINGS.
// Family buckets: COREP (7), Other (4), Benchmark heavy (2).
export const SAMPLE_FILINGS: SampleFiling[] = [
  {
    label: 'COREP_LR — Leverage Ratio',
    family: 'COREP',
    period: '202612',
    url: TD + 'leverage-ratio/DUMMYLEI123456789012.CON_FR_COREP040100_COREPLR_2026-12-31_20251117215412239.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_COREP040100_COREPLR_2026-12-31_20251117215412239.xbrl',
    sizeBytes: 235718,
  },
  {
    label: 'COREP_LE — Large Exposures',
    family: 'COREP',
    period: '202612',
    url: TD + 'corep/DUMMYLEI123456789012.CON_FR_COREP030300_COREPLE_2026-12-31_20251117215146638.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_COREP030300_COREPLE_2026-12-31_20251117215146638.xbrl',
    sizeBytes: 61474,
  },
  {
    label: 'COREP_LCR_DA — LCR (Delegated Act)',
    family: 'COREP',
    period: '202612',
    url: TD + 'corep/DUMMYLEI123456789012.CON_FR_COREP030300_COREPLCRDA_2026-12-31_20251117215032623.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_COREP030300_COREPLCRDA_2026-12-31_20251117215032623.xbrl',
    sizeBytes: 6337836,
  },
  {
    label: 'COREP_NSFR — Net Stable Funding Ratio',
    family: 'COREP',
    period: '202612',
    url: TD + 'corep/DUMMYLEI123456789012.CON_FR_COREP030200_COREPNSFR_2026-12-31_20251117220251891.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_COREP030200_COREPNSFR_2026-12-31_20251117220251891.xbrl',
    sizeBytes: 1509494,
  },
  {
    label: 'COREP_ALM — Additional Liquidity Monitoring',
    family: 'COREP',
    period: '202612',
    url: TD + 'corep/DUMMYLEI123456789012.CON_FR_COREP030300_COREPALM_2026-12-31_20251117220703862.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_COREP030300_COREPALM_2026-12-31_20251117220703862.xbrl',
    sizeBytes: 15847953,
  },
  {
    label: 'COREP_FRTB — Fundamental Review Trading Book',
    family: 'COREP',
    period: '202612',
    url: TD + 'corep/DUMMYLEI123456789012.CON_FR_COREP030400_COREPFRTB_2026-12-31_20251117214636167.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_COREP030400_COREPFRTB_2026-12-31_20251117214636167.xbrl',
    sizeBytes: 180519,
  },
  {
    label: 'COREP_OF — Own Funds',
    family: 'COREP',
    period: '202612',
    url: TD + 'corep/DUMMYLEI123456789012.CON_FR_COREP040100_COREPOF_2026-12-31_20251117214545630.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_COREP040100_COREPOF_2026-12-31_20251117214545630.xbrl',
    sizeBytes: 22933320,
    note: 'Known to exceed 4 GiB WASM cap during validation (spike step 4)',
  },
  {
    label: 'IRRBB — Interest Rate Risk Banking Book',
    family: 'Other',
    period: '202612',
    url: TD + 'other-reports/DUMMYLEI123456789012.CON_FR_IRRBB010200_IRRBB_2026-12-31_20251117224408367.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_IRRBB010200_IRRBB_2026-12-31_20251117224408367.xbrl',
    sizeBytes: 4668347,
  },
  {
    label: 'AE — Asset Encumbrance',
    family: 'Other',
    period: '202612',
    url: TD + 'other-reports/DUMMYLEI123456789012.CON_FR_AE010400_AE_2026-12-31_20251117221254125.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_AE010400_AE_2026-12-31_20251117221254125.xbrl',
    sizeBytes: 1139243,
  },
  {
    label: 'IF_CLASS2 — Investment Firms (class 2)',
    family: 'Other',
    period: '202612',
    url: TD + 'other-reports/DUMMYLEI123456789012.CON_FR_IF010400_IFCLASS2_2026-12-31_20260107124534492.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_IF010400_IFCLASS2_2026-12-31_20260107124534492.xbrl',
    sizeBytes: 5668019,
  },
  {
    label: 'MREL_TLAC — MREL/TLAC reporting',
    family: 'Other',
    period: '202612',
    url: TD + 'other-reports/DUMMYLEI123456789012.CON_FR_MREL010400_MRELTLAC_2026-12-31_20251117225129049.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_MREL010400_MRELTLAC_2026-12-31_20251117225129049.xbrl',
    sizeBytes: 189017,
  },
  {
    label: 'FINREP9 — Financial Reporting (IFRS9)',
    family: 'Benchmark (heavy)',
    period: '202612',
    url: TD + 'finrep/DUMMYLEI123456789012.CON_FR_FINREP030300_FINREP9_2026-12-31_20251117204132986.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_FINREP030300_FINREP9_2026-12-31_20251117204132986.xbrl',
    sizeBytes: 13255484,
    note: 'Expected to exceed 4 GiB WASM cap — measured for completeness',
  },
  {
    label: 'ESG — Pillar 3 ESG disclosure',
    family: 'Benchmark (heavy)',
    period: '202612',
    url: TD + 'other-reports/DUMMYLEI123456789012.CON_FR_ESG010300_ESG_2026-12-31_20251117221911160.xbrl',
    filename: 'DUMMYLEI123456789012.CON_FR_ESG010300_ESG_2026-12-31_20251117221911160.xbrl',
    sizeBytes: 2597525,
    note: 'Smaller instance, but DTS is heavy — measured for completeness',
  },
];

type WorkerResponse =
  | { type: 'ready'; at: number }
  | { type: 'pyodide-loading'; at: number }
  | { type: 'pyodide-loaded'; version: string; loadTimeMs: number }
  | { type: 'pyodide-error'; error: string }
  | { type: 'arelle-loading'; phase: 'packages' | 'micropip' | 'import' }
  | {
      type: 'arelle-loaded';
      arelleVersion: string;
      pythonVersion: string;
      packagesMs: number;
      micropipMs: number;
      importMs: number;
      totalMs: number;
    }
  | { type: 'arelle-error'; error: string; phase: 'packages' | 'micropip' | 'import' }
  | { type: 'taxonomy-loading'; phase: 'fetch' | 'write' | 'register' }
  | {
      type: 'taxonomy-loaded';
      packages: TaxonomyPackageInfo[];
      totalEntryPoints: number;
      totalRemappings: number;
      totalBytes: number;
      workOffline: boolean;
      fetchMs: number;
      writeMs: number;
      registerMs: number;
      totalMs: number;
    }
  | { type: 'taxonomy-error'; error: string; phase: 'fetch' | 'write' | 'register' }
  | {
      type: 'validate-progress';
      id: string;
      phase: ValidatePhase;
      elapsedMs: number;
      usedJSHeapBytes?: number;
    }
  | {
      type: 'validate-result';
      id: string;
      filename: string;
      bytes: number;
      fetchMs: number;
      writeMs: number;
      loadMs: number;
      validateMs: number;
      totalMs: number;
      peakJSHeapBytes: number;
      counts: { error: number; warning: number; info: number; total: number };
      topCodes: Array<{ code: string; level: string; count: number }>;
      messages: ValidationMessage[];
      truncatedAt: number | null;
      modelXbrlFactsCount: number;
      profileStats: ProfileStat[];
      profileTotalSec: number | null;
      wasmHeapBeforeLoadBytes: number | null;
      wasmHeapAfterLoadBytes: number | null;
      wasmHeapAfterValidateBytes: number | null;
      validateFailed: boolean;
      validateException: ValidateException | null;
      modelXbrlErrors: string[];
    }
  | { type: 'validate-error'; id: string; phase: ValidatePhase; error: string }
  | {
      type: 'light-load-progress';
      id: string;
      phase: LightLoadPhase;
      elapsedMs: number;
      usedJSHeapBytes?: number;
    }
  | {
      type: 'light-load-result';
      id: string;
      filename: string;
      bytes: number;
      fetchMs: number;
      writeMs: number;
      parseMs: number;
      labelBuildMs: number;
      labelsBuilt: boolean;
      totalMs: number;
      factsCount: number;
      contextsCount: number;
      unitsCount: number;
      uniqueConceptsCount: number;
      uniqueWithLabel: number;
      uniqueWithoutLabel: number;
      labelIndexSize: number;
      wasmHeapBeforeBytes: number | null;
      wasmHeapAfterParseBytes: number | null;
      wasmHeapAfterLabelsBytes: number | null;
      sampleFacts: LightLoadSampleFact[];
    }
  | { type: 'light-load-error'; id: string; phase: LightLoadPhase; error: string }
  | {
      type: 'trending-test-progress';
      id: string;
      phase: TrendingTestPhase;
      copyIndex: number;
      elapsedMs: number;
    }
  | {
      type: 'trending-test-result';
      id: string;
      filename: string;
      bytes: number;
      copies: number;
      fetchMs: number;
      writeMs: number;
      parseMsPerCopy: number[];
      heapAfterParsePerCopy: number[];
      heapDeltaPerCopy: number[];
      labelBuildMs: number;
      labelsBuilt: boolean;
      totalMs: number;
      factsPerCopy: number[];
      contextsPerCopy: number[];
      unitsPerCopy: number[];
      uniqueConceptsCount: number;
      uniqueWithLabel: number;
      uniqueWithoutLabel: number;
      labelIndexSize: number;
      heapBeforeBytes: number | null;
      heapAfterLabelsBytes: number | null;
    }
  | {
      type: 'trending-test-error';
      id: string;
      phase: TrendingTestPhase;
      copyIndex: number;
      error: string;
    }
  | {
      type: 'reports-progress';
      id: string;
      phase: ReportsPhase;
      elapsedMs: number;
      usedJSHeapBytes?: number;
    }
  | {
      type: 'reports-result';
      id: string;
      sourceFilename: string;
      downloadFilename: string;
      xlsxBytes: Uint8Array;
      fetchMs: number;
      writeMs: number;
      loadMs: number;
      exportMs: number;
      totalMs: number;
      tableCount: number;
      filedIndicatorCount: number;
      debugRows: ReportsDebugRow[];
      debugStats: ReportsDebugStats;
      wasmHeapBeforeLoadBytes: number | null;
      wasmHeapAfterLoadBytes: number | null;
      wasmHeapAfterExportBytes: number | null;
    }
  | { type: 'reports-error'; id: string; phase: ReportsPhase; error: string }
  | { type: 'pong'; receivedAt: number; payload?: unknown };

@Component({
  selector: 'app-dev-spike',
  imports: [JobStatusLineComponent],
  templateUrl: './dev-spike.component.html',
  styles: [
    `
      :host {
        display: block;
      }
      .spike-shell {
        max-width: 1200px;
      }
    `,
  ],
})
export class DevSpikeComponent implements OnInit, OnDestroy {
  private readonly bridge = inject(XbrlWorkerBridgeService);
  private messageSub?: Subscription;

  protected readonly title = signal('Developer — spike lab');
  protected readonly workerStatus = signal<WorkerStatus>('idle');
  protected readonly lastResponse = signal<string>('');

  protected readonly pyodideStatus = signal<PyodideStatus>('idle');
  protected readonly pyodideVersion = signal<string>('');
  protected readonly pyodideLoadMs = signal<number | null>(null);
  protected readonly pyodideError = signal<string>('');

  protected readonly arelleStatus = signal<ArelleStatus>('idle');
  protected readonly arelleVersion = signal<string>('');
  protected readonly pythonVersion = signal<string>('');
  protected readonly arellePackagesMs = signal<number | null>(null);
  protected readonly arelleMicropipMs = signal<number | null>(null);
  protected readonly arelleImportMs = signal<number | null>(null);
  protected readonly arelleTotalMs = signal<number | null>(null);
  protected readonly arelleError = signal<string>('');
  protected readonly arelleErrorPhase = signal<string>('');

  protected readonly taxonomyStatus = signal<TaxonomyStatus>('idle');
  protected readonly taxonomyPackages = signal<TaxonomyPackageInfo[]>([]);
  protected readonly taxonomyTotalEntryPoints = signal<number | null>(null);
  protected readonly taxonomyTotalRemappings = signal<number | null>(null);
  protected readonly taxonomyTotalBytes = signal<number | null>(null);
  protected readonly taxonomyWorkOffline = signal<boolean | null>(null);
  protected readonly taxonomyFetchMs = signal<number | null>(null);
  protected readonly taxonomyWriteMs = signal<number | null>(null);
  protected readonly taxonomyRegisterMs = signal<number | null>(null);
  protected readonly taxonomyTotalMs = signal<number | null>(null);
  protected readonly taxonomyError = signal<string>('');
  protected readonly taxonomyErrorPhase = signal<string>('');

  protected readonly coldStartTotalMs = signal<number | null>(null);

  protected readonly samples = signal<SampleFiling[]>(SAMPLE_FILINGS);
  protected readonly groupedSamples = computed<
    Array<{ family: string; samples: SampleFiling[] }>
  >(() => {
    const groups = new Map<string, SampleFiling[]>();
    for (const s of this.samples()) {
      const existing = groups.get(s.family) ?? [];
      existing.push(s);
      groups.set(s.family, existing);
    }
    return [...groups.entries()].map(([family, samples]) => ({ family, samples }));
  });
  protected readonly validateStatus = signal<ValidateStatus>('idle');
  protected readonly validateCurrentId = signal<string>('');
  protected readonly validateElapsedMs = signal<number | null>(null);
  protected readonly validateUsedHeapBytes = signal<number | null>(null);
  protected readonly validateFilename = signal<string>('');
  protected readonly validateBytes = signal<number | null>(null);
  protected readonly validateFetchMs = signal<number | null>(null);
  protected readonly validateWriteMs = signal<number | null>(null);
  protected readonly validateLoadMs = signal<number | null>(null);
  protected readonly validateValidateMs = signal<number | null>(null);
  protected readonly validateTotalMs = signal<number | null>(null);
  protected readonly validatePeakHeapBytes = signal<number | null>(null);
  protected readonly validateCounts = signal<{
    error: number;
    warning: number;
    info: number;
    total: number;
  } | null>(null);
  protected readonly validateTopCodes = signal<
    Array<{ code: string; level: string; count: number }>
  >([]);
  protected readonly validateMessages = signal<ValidationMessage[]>([]);
  protected readonly validateTruncatedAt = signal<number | null>(null);
  protected readonly validateFactsCount = signal<number | null>(null);
  protected readonly validateProfileStats = signal<ProfileStat[]>([]);
  protected readonly validateProfileTotalSec = signal<number | null>(null);
  protected readonly wasmHeapBeforeLoadBytes = signal<number | null>(null);
  protected readonly wasmHeapAfterLoadBytes = signal<number | null>(null);
  protected readonly wasmHeapAfterValidateBytes = signal<number | null>(null);
  protected readonly validateFailed = signal<boolean>(false);
  protected readonly validateException = signal<ValidateException | null>(null);
  protected readonly modelXbrlErrors = signal<string[]>([]);
  protected readonly validateError = signal<string>('');
  protected readonly validateErrorPhase = signal<string>('');

  protected readonly lightLoadStatus = signal<LightLoadStatus>('idle');
  protected readonly lightLoadCurrentId = signal<string>('');
  protected readonly lightLoadFilename = signal<string>('');
  protected readonly lightLoadElapsedMs = signal<number | null>(null);
  protected readonly lightLoadBytes = signal<number | null>(null);
  protected readonly lightLoadFetchMs = signal<number | null>(null);
  protected readonly lightLoadWriteMs = signal<number | null>(null);
  protected readonly lightLoadParseMs = signal<number | null>(null);
  protected readonly lightLoadLabelBuildMs = signal<number | null>(null);
  protected readonly lightLoadLabelsBuilt = signal<boolean>(false);
  protected readonly lightLoadTotalMs = signal<number | null>(null);
  protected readonly lightLoadFactsCount = signal<number | null>(null);
  protected readonly lightLoadContextsCount = signal<number | null>(null);
  protected readonly lightLoadUnitsCount = signal<number | null>(null);
  protected readonly lightLoadUniqueConceptsCount = signal<number | null>(null);
  protected readonly lightLoadUniqueWithLabel = signal<number | null>(null);
  protected readonly lightLoadUniqueWithoutLabel = signal<number | null>(null);
  protected readonly lightLoadLabelIndexSize = signal<number | null>(null);
  protected readonly lightLoadHeapBeforeBytes = signal<number | null>(null);
  protected readonly lightLoadHeapAfterParseBytes = signal<number | null>(null);
  protected readonly lightLoadHeapAfterLabelsBytes = signal<number | null>(null);
  protected readonly lightLoadSampleFacts = signal<LightLoadSampleFact[]>([]);
  protected readonly lightLoadError = signal<string>('');
  protected readonly lightLoadErrorPhase = signal<string>('');

  protected readonly trendingTestStatus = signal<TrendingTestStatus>('idle');
  protected readonly trendingTestCurrentId = signal<string>('');
  protected readonly trendingTestFilename = signal<string>('');
  protected readonly trendingTestCopyIndex = signal<number | null>(null);
  protected readonly trendingTestElapsedMs = signal<number | null>(null);
  protected readonly trendingTestBytes = signal<number | null>(null);
  protected readonly trendingTestCopies = signal<number | null>(null);
  protected readonly trendingTestFetchMs = signal<number | null>(null);
  protected readonly trendingTestWriteMs = signal<number | null>(null);
  protected readonly trendingTestParseMsPerCopy = signal<number[]>([]);
  protected readonly trendingTestHeapAfterParsePerCopy = signal<number[]>([]);
  protected readonly trendingTestHeapDeltaPerCopy = signal<number[]>([]);
  protected readonly trendingTestLabelBuildMs = signal<number | null>(null);
  protected readonly trendingTestLabelsBuilt = signal<boolean>(false);
  protected readonly trendingTestTotalMs = signal<number | null>(null);
  protected readonly trendingTestFactsPerCopy = signal<number[]>([]);
  protected readonly trendingTestContextsPerCopy = signal<number[]>([]);
  protected readonly trendingTestUnitsPerCopy = signal<number[]>([]);
  protected readonly trendingTestUniqueConceptsCount = signal<number | null>(null);
  protected readonly trendingTestUniqueWithLabel = signal<number | null>(null);
  protected readonly trendingTestUniqueWithoutLabel = signal<number | null>(null);
  protected readonly trendingTestLabelIndexSize = signal<number | null>(null);
  protected readonly trendingTestHeapBeforeBytes = signal<number | null>(null);
  protected readonly trendingTestHeapAfterLabelsBytes = signal<number | null>(null);
  protected readonly trendingTestError = signal<string>('');
  protected readonly trendingTestErrorPhase = signal<string>('');

  protected readonly reportsStatus = signal<ReportsStatus>('idle');
  protected readonly reportsCurrentId = signal<string>('');
  protected readonly reportsFilename = signal<string>('');
  protected readonly reportsDownloadFilename = signal<string>('');
  protected readonly reportsElapsedMs = signal<number | null>(null);
  protected readonly reportsFetchMs = signal<number | null>(null);
  protected readonly reportsWriteMs = signal<number | null>(null);
  protected readonly reportsLoadMs = signal<number | null>(null);
  protected readonly reportsExportMs = signal<number | null>(null);
  protected readonly reportsTotalMs = signal<number | null>(null);
  protected readonly reportsTableCount = signal<number | null>(null);
  protected readonly reportsFiledIndicatorCount = signal<number | null>(null);
  protected readonly reportsWasmHeapBeforeLoadBytes = signal<number | null>(null);
  protected readonly reportsWasmHeapAfterLoadBytes = signal<number | null>(null);
  protected readonly reportsWasmHeapAfterExportBytes = signal<number | null>(null);
  protected readonly reportsDebugRows = signal<ReportsDebugRow[]>([]);
  protected readonly reportsDebugStats = signal<ReportsDebugStats | null>(null);
  protected readonly reportsXlsxReady = signal(false);
  private reportsXlsxBytes: Uint8Array | null = null;
  protected readonly reportsError = signal<string>('');
  protected readonly reportsErrorPhase = signal<string>('');
  protected readonly reportsDebugFilledCount = computed(
    () =>
      this.reportsDebugRows().filter(
        (r) => r.value !== null && r.value !== undefined && String(r.value).trim() !== '',
      ).length,
  );
  protected readonly reportsDebugSlotCount = computed(() => this.reportsDebugRows().length);

  protected reportsStatsHasOpenTables(stats: ReportsDebugStats): boolean {
    return stats.tables.some((t) => t.openTable);
  }

  private readonly noJobRunning = computed(
    () =>
      this.workerStatus() === 'ready' &&
      this.pyodideStatus() === 'ready' &&
      this.arelleStatus() === 'ready' &&
      this.taxonomyStatus() === 'ready' &&
      this.validateStatus() !== 'fetch' &&
      this.validateStatus() !== 'write' &&
      this.validateStatus() !== 'load' &&
      this.validateStatus() !== 'validate' &&
      this.lightLoadStatus() !== 'fetch' &&
      this.lightLoadStatus() !== 'write' &&
      this.lightLoadStatus() !== 'parse' &&
      this.lightLoadStatus() !== 'labels' &&
      this.trendingTestStatus() !== 'fetch' &&
      this.trendingTestStatus() !== 'write' &&
      this.trendingTestStatus() !== 'parse' &&
      this.trendingTestStatus() !== 'labels' &&
      this.reportsStatus() !== 'fetch' &&
      this.reportsStatus() !== 'write' &&
      this.reportsStatus() !== 'parse' &&
      this.reportsStatus() !== 'indicators' &&
      this.reportsStatus() !== 'render' &&
      this.reportsStatus() !== 'write-xlsx',
  );

  protected readonly canValidate = this.noJobRunning;
  protected readonly canLightLoad = this.noJobRunning;
  protected readonly canTrendingTest = this.noJobRunning;
  protected readonly canReports = this.noJobRunning;

  protected readonly criterion2WithinThreshold = computed(() => {
    const total = this.validateTotalMs();
    if (total === null) return null;
    return total <= 120_000;
  });

  ngOnInit(): void {
    if (!this.bridge.supported) {
      this.workerStatus.set('unsupported');
      return;
    }

    this.messageSub = this.bridge.messages$.subscribe((raw) => {
      if ((raw as { type?: string }).type === 'worker-error') {
        this.workerStatus.set('error');
        return;
      }
      const msg = raw as WorkerResponse;
      switch (msg.type) {
          case 'ready':
            this.workerStatus.set('ready');
            this.bridge.post({ type: 'ping', payload: 'hello from main thread' });
            return;
          case 'pong':
            this.lastResponse.set(JSON.stringify(msg));
            return;
          case 'pyodide-loading':
            this.pyodideStatus.set('loading');
            return;
          case 'pyodide-loaded':
            this.pyodideStatus.set('ready');
            this.pyodideVersion.set(msg.version);
            this.pyodideLoadMs.set(msg.loadTimeMs);
            return;
          case 'pyodide-error':
            this.pyodideStatus.set('error');
            this.pyodideError.set(msg.error);
            return;
          case 'arelle-loading':
            this.arelleStatus.set(msg.phase);
            return;
          case 'arelle-loaded':
            this.arelleStatus.set('ready');
            this.arelleVersion.set(msg.arelleVersion);
            this.pythonVersion.set(msg.pythonVersion);
            this.arellePackagesMs.set(msg.packagesMs);
            this.arelleMicropipMs.set(msg.micropipMs);
            this.arelleImportMs.set(msg.importMs);
            this.arelleTotalMs.set(msg.totalMs);
            this.coldStartTotalMs.set((this.pyodideLoadMs() ?? 0) + msg.totalMs);
            return;
          case 'arelle-error':
            this.arelleStatus.set('error');
            this.arelleError.set(msg.error);
            this.arelleErrorPhase.set(msg.phase);
            return;
          case 'taxonomy-loading':
            this.taxonomyStatus.set(msg.phase);
            return;
          case 'taxonomy-loaded':
            this.taxonomyStatus.set('ready');
            this.taxonomyPackages.set(msg.packages);
            this.taxonomyTotalEntryPoints.set(msg.totalEntryPoints);
            this.taxonomyTotalRemappings.set(msg.totalRemappings);
            this.taxonomyTotalBytes.set(msg.totalBytes);
            this.taxonomyWorkOffline.set(msg.workOffline);
            this.taxonomyFetchMs.set(msg.fetchMs);
            this.taxonomyWriteMs.set(msg.writeMs);
            this.taxonomyRegisterMs.set(msg.registerMs);
            this.taxonomyTotalMs.set(msg.totalMs);
            return;
          case 'taxonomy-error':
            this.taxonomyStatus.set('error');
            this.taxonomyError.set(msg.error);
            this.taxonomyErrorPhase.set(msg.phase);
            return;
          case 'validate-progress':
            if (msg.id !== this.validateCurrentId()) return;
            this.validateStatus.set(msg.phase);
            this.validateElapsedMs.set(msg.elapsedMs);
            if (msg.usedJSHeapBytes !== undefined) {
              this.validateUsedHeapBytes.set(msg.usedJSHeapBytes);
            }
            return;
          case 'validate-result':
            if (msg.id !== this.validateCurrentId()) return;
            this.validateStatus.set('ready');
            this.validateBytes.set(msg.bytes);
            this.validateFetchMs.set(msg.fetchMs);
            this.validateWriteMs.set(msg.writeMs);
            this.validateLoadMs.set(msg.loadMs);
            this.validateValidateMs.set(msg.validateMs);
            this.validateTotalMs.set(msg.totalMs);
            this.validatePeakHeapBytes.set(msg.peakJSHeapBytes);
            this.validateCounts.set(msg.counts);
            this.validateTopCodes.set(msg.topCodes);
            this.validateMessages.set(msg.messages);
            this.validateTruncatedAt.set(msg.truncatedAt);
            this.validateFactsCount.set(msg.modelXbrlFactsCount);
            this.validateProfileStats.set(msg.profileStats);
            this.validateProfileTotalSec.set(msg.profileTotalSec);
            this.wasmHeapBeforeLoadBytes.set(msg.wasmHeapBeforeLoadBytes);
            this.wasmHeapAfterLoadBytes.set(msg.wasmHeapAfterLoadBytes);
            this.wasmHeapAfterValidateBytes.set(msg.wasmHeapAfterValidateBytes);
            this.validateFailed.set(msg.validateFailed);
            this.validateException.set(msg.validateException);
            this.modelXbrlErrors.set(msg.modelXbrlErrors);
            return;
          case 'validate-error':
            if (msg.id !== this.validateCurrentId()) return;
            this.validateStatus.set('error');
            this.validateError.set(msg.error);
            this.validateErrorPhase.set(msg.phase);
            return;
          case 'light-load-progress':
            if (msg.id !== this.lightLoadCurrentId()) return;
            this.lightLoadStatus.set(msg.phase);
            this.lightLoadElapsedMs.set(msg.elapsedMs);
            return;
          case 'light-load-result':
            if (msg.id !== this.lightLoadCurrentId()) return;
            this.lightLoadStatus.set('ready');
            this.lightLoadBytes.set(msg.bytes);
            this.lightLoadFetchMs.set(msg.fetchMs);
            this.lightLoadWriteMs.set(msg.writeMs);
            this.lightLoadParseMs.set(msg.parseMs);
            this.lightLoadLabelBuildMs.set(msg.labelBuildMs);
            this.lightLoadLabelsBuilt.set(msg.labelsBuilt);
            this.lightLoadTotalMs.set(msg.totalMs);
            this.lightLoadFactsCount.set(msg.factsCount);
            this.lightLoadContextsCount.set(msg.contextsCount);
            this.lightLoadUnitsCount.set(msg.unitsCount);
            this.lightLoadUniqueConceptsCount.set(msg.uniqueConceptsCount);
            this.lightLoadUniqueWithLabel.set(msg.uniqueWithLabel);
            this.lightLoadUniqueWithoutLabel.set(msg.uniqueWithoutLabel);
            this.lightLoadLabelIndexSize.set(msg.labelIndexSize);
            this.lightLoadHeapBeforeBytes.set(msg.wasmHeapBeforeBytes);
            this.lightLoadHeapAfterParseBytes.set(msg.wasmHeapAfterParseBytes);
            this.lightLoadHeapAfterLabelsBytes.set(msg.wasmHeapAfterLabelsBytes);
            this.lightLoadSampleFacts.set(msg.sampleFacts);
            return;
          case 'light-load-error':
            if (msg.id !== this.lightLoadCurrentId()) return;
            this.lightLoadStatus.set('error');
            this.lightLoadError.set(msg.error);
            this.lightLoadErrorPhase.set(msg.phase);
            return;
          case 'trending-test-progress':
            if (msg.id !== this.trendingTestCurrentId()) return;
            this.trendingTestStatus.set(msg.phase);
            this.trendingTestCopyIndex.set(msg.copyIndex);
            this.trendingTestElapsedMs.set(msg.elapsedMs);
            return;
          case 'trending-test-result':
            if (msg.id !== this.trendingTestCurrentId()) return;
            this.trendingTestStatus.set('ready');
            this.trendingTestBytes.set(msg.bytes);
            this.trendingTestCopies.set(msg.copies);
            this.trendingTestFetchMs.set(msg.fetchMs);
            this.trendingTestWriteMs.set(msg.writeMs);
            this.trendingTestParseMsPerCopy.set(msg.parseMsPerCopy);
            this.trendingTestHeapAfterParsePerCopy.set(msg.heapAfterParsePerCopy);
            this.trendingTestHeapDeltaPerCopy.set(msg.heapDeltaPerCopy);
            this.trendingTestLabelBuildMs.set(msg.labelBuildMs);
            this.trendingTestLabelsBuilt.set(msg.labelsBuilt);
            this.trendingTestTotalMs.set(msg.totalMs);
            this.trendingTestFactsPerCopy.set(msg.factsPerCopy);
            this.trendingTestContextsPerCopy.set(msg.contextsPerCopy);
            this.trendingTestUnitsPerCopy.set(msg.unitsPerCopy);
            this.trendingTestUniqueConceptsCount.set(msg.uniqueConceptsCount);
            this.trendingTestUniqueWithLabel.set(msg.uniqueWithLabel);
            this.trendingTestUniqueWithoutLabel.set(msg.uniqueWithoutLabel);
            this.trendingTestLabelIndexSize.set(msg.labelIndexSize);
            this.trendingTestHeapBeforeBytes.set(msg.heapBeforeBytes);
            this.trendingTestHeapAfterLabelsBytes.set(msg.heapAfterLabelsBytes);
            return;
          case 'trending-test-error':
            if (msg.id !== this.trendingTestCurrentId()) return;
            this.trendingTestStatus.set('error');
            this.trendingTestError.set(msg.error);
            this.trendingTestErrorPhase.set(msg.phase);
            return;
          case 'reports-progress':
            if (msg.id !== this.reportsCurrentId()) return;
            this.reportsStatus.set(msg.phase);
            this.reportsElapsedMs.set(msg.elapsedMs);
            return;
          case 'reports-result':
            if (msg.id !== this.reportsCurrentId()) return;
            this.reportsStatus.set('ready');
            this.reportsDownloadFilename.set(msg.downloadFilename);
            this.reportsFetchMs.set(msg.fetchMs);
            this.reportsWriteMs.set(msg.writeMs);
            this.reportsLoadMs.set(msg.loadMs);
            this.reportsExportMs.set(msg.exportMs);
            this.reportsTotalMs.set(msg.totalMs);
            this.reportsTableCount.set(msg.tableCount);
            this.reportsFiledIndicatorCount.set(msg.filedIndicatorCount);
            this.reportsWasmHeapBeforeLoadBytes.set(msg.wasmHeapBeforeLoadBytes);
            this.reportsWasmHeapAfterLoadBytes.set(msg.wasmHeapAfterLoadBytes);
            this.reportsWasmHeapAfterExportBytes.set(msg.wasmHeapAfterExportBytes);
            this.reportsDebugRows.set(msg.debugRows ?? []);
            this.reportsDebugStats.set(msg.debugStats ?? null);
            logReportsMetricDiagnostics(msg.debugStats ?? null);
            logReportsOpenTableDiagnostics(msg.debugStats ?? null);
            this.reportsXlsxBytes = msg.xlsxBytes;
            this.reportsXlsxReady.set(true);
            return;
          case 'reports-error':
            if (msg.id !== this.reportsCurrentId()) return;
            this.reportsStatus.set('error');
            this.reportsError.set(msg.error);
            this.reportsErrorPhase.set(msg.phase);
            return;
        }
    });
  }

  ngOnDestroy(): void {
    this.messageSub?.unsubscribe();
  }

  protected formatMs(ms: number): string {
    return Math.round(ms).toLocaleString('en-US');
  }

  protected formatMem(kb: number | null): string {
    return kb === null ? '—' : Math.round(kb).toLocaleString('en-US');
  }

  protected shareOfValidate(ms: number): string {
    const total = this.validateValidateMs();
    if (!total || total <= 0) return '';
    return ((ms / total) * 100).toFixed(1) + '%';
  }

  protected formatBytesMB(bytes: number | null): string {
    if (bytes === null) return '—';
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(1) + ' MiB';
  }

  protected wasmHeapGrowthLoadMB(): string {
    const before = this.wasmHeapBeforeLoadBytes();
    const after = this.wasmHeapAfterLoadBytes();
    if (before === null || after === null) return '—';
    return ((after - before) / (1024 * 1024)).toFixed(1) + ' MiB';
  }

  protected wasmHeapGrowthValidateMB(): string {
    const before = this.wasmHeapAfterLoadBytes();
    const after = this.wasmHeapAfterValidateBytes();
    if (before === null || after === null) return '—';
    return ((after - before) / (1024 * 1024)).toFixed(1) + ' MiB';
  }

  protected validateSample(sample: SampleFiling): void {
    if (!this.bridge.supported || !this.canValidate()) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.validateCurrentId.set(id);
    this.validateStatus.set('fetch');
    this.validateFilename.set(sample.filename);
    this.validateElapsedMs.set(0);
    this.validateUsedHeapBytes.set(null);
    this.validateBytes.set(null);
    this.validateFetchMs.set(null);
    this.validateWriteMs.set(null);
    this.validateLoadMs.set(null);
    this.validateValidateMs.set(null);
    this.validateTotalMs.set(null);
    this.validatePeakHeapBytes.set(null);
    this.validateCounts.set(null);
    this.validateTopCodes.set([]);
    this.validateMessages.set([]);
    this.validateTruncatedAt.set(null);
    this.validateFactsCount.set(null);
    this.validateProfileStats.set([]);
    this.validateProfileTotalSec.set(null);
    this.wasmHeapBeforeLoadBytes.set(null);
    this.wasmHeapAfterLoadBytes.set(null);
    this.wasmHeapAfterValidateBytes.set(null);
    this.validateFailed.set(false);
    this.validateException.set(null);
    this.modelXbrlErrors.set([]);
    this.validateError.set('');
    this.validateErrorPhase.set('');
    this.bridge.post({
      type: 'validate-request',
      id,
      url: sample.url,
      filename: sample.filename,
    });
  }

  protected lightLoadSample(sample: SampleFiling): void {
    if (!this.bridge.supported || !this.canLightLoad()) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.lightLoadCurrentId.set(id);
    this.lightLoadStatus.set('fetch');
    this.lightLoadFilename.set(sample.filename);
    this.lightLoadElapsedMs.set(0);
    this.lightLoadBytes.set(null);
    this.lightLoadFetchMs.set(null);
    this.lightLoadWriteMs.set(null);
    this.lightLoadParseMs.set(null);
    this.lightLoadLabelBuildMs.set(null);
    this.lightLoadLabelsBuilt.set(false);
    this.lightLoadTotalMs.set(null);
    this.lightLoadFactsCount.set(null);
    this.lightLoadContextsCount.set(null);
    this.lightLoadUnitsCount.set(null);
    this.lightLoadUniqueConceptsCount.set(null);
    this.lightLoadUniqueWithLabel.set(null);
    this.lightLoadUniqueWithoutLabel.set(null);
    this.lightLoadLabelIndexSize.set(null);
    this.lightLoadHeapBeforeBytes.set(null);
    this.lightLoadHeapAfterParseBytes.set(null);
    this.lightLoadHeapAfterLabelsBytes.set(null);
    this.lightLoadSampleFacts.set([]);
    this.lightLoadError.set('');
    this.lightLoadErrorPhase.set('');
    this.bridge.post({
      type: 'light-load-request',
      id,
      url: sample.url,
      filename: sample.filename,
    });
  }

  protected trendingTestSample(sample: SampleFiling): void {
    if (!this.bridge.supported || !this.canTrendingTest()) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.trendingTestCurrentId.set(id);
    this.trendingTestStatus.set('fetch');
    this.trendingTestFilename.set(sample.filename);
    this.trendingTestCopyIndex.set(null);
    this.trendingTestElapsedMs.set(0);
    this.trendingTestBytes.set(null);
    this.trendingTestCopies.set(null);
    this.trendingTestFetchMs.set(null);
    this.trendingTestWriteMs.set(null);
    this.trendingTestParseMsPerCopy.set([]);
    this.trendingTestHeapAfterParsePerCopy.set([]);
    this.trendingTestHeapDeltaPerCopy.set([]);
    this.trendingTestLabelBuildMs.set(null);
    this.trendingTestLabelsBuilt.set(false);
    this.trendingTestTotalMs.set(null);
    this.trendingTestFactsPerCopy.set([]);
    this.trendingTestContextsPerCopy.set([]);
    this.trendingTestUnitsPerCopy.set([]);
    this.trendingTestUniqueConceptsCount.set(null);
    this.trendingTestUniqueWithLabel.set(null);
    this.trendingTestUniqueWithoutLabel.set(null);
    this.trendingTestLabelIndexSize.set(null);
    this.trendingTestHeapBeforeBytes.set(null);
    this.trendingTestHeapAfterLabelsBytes.set(null);
    this.trendingTestError.set('');
    this.trendingTestErrorPhase.set('');
    this.bridge.post({
      type: 'trending-test-request',
      id,
      url: sample.url,
      filename: sample.filename,
      copies: 2,
    });
  }

  protected exportReportsSample(sample: SampleFiling): void {
    if (!this.bridge.supported || !this.canReports()) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.reportsCurrentId.set(id);
    this.reportsStatus.set('fetch');
    this.reportsFilename.set(sample.filename);
    this.reportsDownloadFilename.set('');
    this.reportsElapsedMs.set(0);
    this.reportsFetchMs.set(null);
    this.reportsWriteMs.set(null);
    this.reportsLoadMs.set(null);
    this.reportsExportMs.set(null);
    this.reportsTotalMs.set(null);
    this.reportsTableCount.set(null);
    this.reportsFiledIndicatorCount.set(null);
    this.reportsWasmHeapBeforeLoadBytes.set(null);
    this.reportsWasmHeapAfterLoadBytes.set(null);
    this.reportsWasmHeapAfterExportBytes.set(null);
    this.reportsDebugRows.set([]);
    this.reportsDebugStats.set(null);
    this.reportsXlsxBytes = null;
    this.reportsXlsxReady.set(false);
    this.reportsError.set('');
    this.reportsErrorPhase.set('');
    this.bridge.post({
      type: 'reports-request',
      id,
      url: sample.url,
      filename: sample.filename,
    });
  }

  protected downloadReports(): void {
    const filename = this.reportsDownloadFilename();
    const bytes = this.reportsXlsxBytes;
    if (!filename || !bytes) return;
    this.downloadReportsFile(filename, bytes);
  }

  protected formatReportsDebugValue(value: string | number | null): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  private downloadReportsFile(filename: string, bytes: Uint8Array): void {
    // Worker transfer may use ArrayBufferLike; Blob expects a plain ArrayBuffer view.
    const copy = bytes.slice();
    const blob = new Blob([copy], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
